import { ConfirmDialog, FOCUS_RING, MultilineInput } from '@amelio/ui-library';
import { useEffect, useRef, useState } from 'react';

import type { EmployeeNoteUpdateRequest } from '@/types/employee-note';

import {
    useCreateEmployeeNote,
    useDeleteEmployeeNote,
    useUpdateEmployeeNote,
} from '@/api/performance-profile';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import {
    noteHtmlToPlainText,
    plainTextToNoteHtml,
} from '@/pages/ManagerAssessment/utils/assessmentNoteText';
import { useAssessmentNotes } from '@/pages/ManagerAssessment/utils/useAssessmentNotes';
import { ASSESSMENT_NOTE_DEFAULT_CATEGORY } from '@/types/employee-note';

interface AssessmentQuestionNoteProps {
    /** The assessed employee: the note is theirs, filed in their profile. */
    employeeId: string | undefined;
    cycleInstanceId: string | undefined;
    questionId: string;
    /** A completed evaluation shows its notes but takes no new ones and no edits. */
    readOnly?: boolean;
    dataTestId?: string;
}

const SAVED_INDICATOR_DURATION_MS = 3000;

const NOTE_CAPTION = 'text-font-xs text-white/60';

/**
 * The note a manager keeps under one question of the assessment. It is not part of the answer: it
 * is a note of the employee's profile, linked to the assessment and to the question,
 * so the question is where the note is taken, never where it lives.
 */
export const AssessmentQuestionNote = ({
    employeeId,
    cycleInstanceId,
    questionId,
    readOnly = false,
    dataTestId = 'assessment-question-note',
}: AssessmentQuestionNoteProps) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();

    const {
        data: notesPage,
        isPending: isLookupPending,
        isError: hasLookupFailed,
        isPlaceholderData: isShowingAnotherRead,
    } = useAssessmentNotes(employeeId, cycleInstanceId);
    const linkedNote = notesPage?.items.find((note) => note.reviewQuestionId === questionId);
    const savedText = noteHtmlToPlainText(linkedNote?.content ?? '');

    const createNote = useCreateEmployeeNote(employeeId);
    const updateNote = useUpdateEmployeeNote(employeeId);
    const deleteNote = useDeleteEmployeeNote(employeeId);
    const isSaving = createNote.isPending || updateNote.isPending || deleteNote.isPending;

    // `linkedNote` is undefined both before the lookup answers and when there is no note, and the
    // save below creates or updates depending on it: nothing is written until the lookup has said,
    // for this very assessment (a previous read kept on screen does not count), and nothing while a
    // save is still being read back, which would otherwise write a second note.
    const isNoteStateKnown = !isLookupPending && !hasLookupFailed && !isShowingAnotherRead;
    const canWrite = !readOnly && isNoteStateKnown && !isSaving;
    const isWrittenBySomeoneElse = linkedNote !== undefined && !linkedNote.canEdit;

    const [text, setText] = useState(savedText);
    const [isExpanded, setIsExpanded] = useState(false);
    const [savedRecently, setSavedRecently] = useState(false);
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const lastSavedTextRef = useRef(savedText);

    // Follows the stored note only while the field still shows the previous one: text typed since
    // is never overwritten by a note that arrives late.
    useEffect(() => {
        setText((current) => (current === lastSavedTextRef.current ? savedText : current));
        lastSavedTextRef.current = savedText;
    }, [savedText]);

    useEffect(() => () => clearTimeout(savedTimeoutRef.current), []);

    const reportFailure = (error: Error) =>
        handleError({
            error,
            data: {
                title: t('errors.genericTitle'),
                message: t('managerAssessmentEvaluation.survey.notesSaveError'),
            },
        });

    const saveCallbacks = {
        onSuccess: () => {
            setSavedRecently(true);
            clearTimeout(savedTimeoutRef.current);
            savedTimeoutRef.current = setTimeout(
                () => setSavedRecently(false),
                SAVED_INDICATOR_DURATION_MS,
            );
        },
        onError: reportFailure,
    };

    const buildRequest = (): EmployeeNoteUpdateRequest => ({
        // A note moved to another folder from the profile stays there when it is edited here.
        category: linkedNote?.category ?? ASSESSMENT_NOTE_DEFAULT_CATEGORY,
        content: plainTextToNoteHtml(text),
    });

    // Emptying the field asks before removing the note: it outlives the assessment and is read in
    // the profile, so a stray "select all + delete" must not destroy it on the way out of the field.
    const handleBlur = () => {
        if (!canWrite || text === savedText) {
            return;
        }

        const isEmpty = text.trim() === '';

        if (!linkedNote) {
            if (!isEmpty) {
                createNote.mutate(
                    { ...buildRequest(), cycleInstanceId, reviewQuestionId: questionId },
                    saveCallbacks,
                );
            }
            return;
        }

        if (isEmpty) {
            setIsConfirmingDelete(true);
            return;
        }

        updateNote.mutate({ noteId: linkedNote.id, request: buildRequest() }, saveCallbacks);
    };

    const handleConfirmDelete = () => {
        if (!linkedNote) {
            return;
        }

        deleteNote.mutate(linkedNote.id, {
            onSuccess: () => {
                setIsConfirmingDelete(false);
                // The field stays open, so focus does not fall back to the page.
                setIsExpanded(true);
                saveCallbacks.onSuccess();
            },
            onError: (error) => {
                setIsConfirmingDelete(false);
                setText(savedText);
                reportFailure(error);
            },
        });
    };

    const handleCancelDelete = () => {
        setText(savedText);
        setIsConfirmingDelete(false);
    };

    const statusMessage = hasLookupFailed
        ? t('managerAssessmentEvaluation.survey.notesUnavailable')
        : isSaving
          ? t('managerAssessmentEvaluation.survey.notesSaving')
          : savedRecently
            ? t('managerAssessmentEvaluation.survey.notesSaved')
            : '';

    const renderNote = () => {
        // A note that cannot be changed is there to be read: as text, not as a disabled field that
        // drops out of the tab order and fades on the dark background.
        if (isWrittenBySomeoneElse || readOnly) {
            return (
                <>
                    <p
                        className={
                            'text-font-sm rounded-lg border border-white/40 bg-white/10 p-3 whitespace-pre-line text-white'
                        }
                        data-testid={`${dataTestId}-text`}
                    >
                        {savedText}
                    </p>
                    {isWrittenBySomeoneElse && (
                        <p className={NOTE_CAPTION}>
                            {t('managerAssessmentEvaluation.survey.notesReadOnly')}
                        </p>
                    )}
                </>
            );
        }

        return (
            <MultilineInput
                value={text}
                onChange={setText}
                onBlur={handleBlur}
                // A field that accepts text it cannot save is worse than one that waits.
                disabled={!canWrite}
                placeholder={t('managerAssessmentEvaluation.survey.notesPlaceholder')}
                ariaLabel={t('managerAssessmentEvaluation.survey.notesLabel')}
                rows={3}
                autoGrow
                textColour={'transparent'}
                className={
                    'text-font-sm w-full rounded-lg border border-white/40 bg-white/10 p-3 text-white placeholder:text-white/40 focus-within:border-teal-400 focus-within:bg-white/15'
                }
                dataTestId={`${dataTestId}-input`}
            />
        );
    };

    if (readOnly && !linkedNote) {
        return null;
    }

    return (
        <div
            className={'flex flex-col gap-2'}
            data-testid={dataTestId}
        >
            {isExpanded || linkedNote ? (
                <>
                    {renderNote()}
                    <p className={NOTE_CAPTION}>
                        {t('managerAssessmentEvaluation.survey.notesDisclaimer')}
                    </p>
                </>
            ) : (
                <button
                    type={'button'}
                    onClick={() => setIsExpanded(true)}
                    className={`${FOCUS_RING} text-font-sm w-fit rounded-lg py-1 text-left font-normal text-white/60 underline transition-colors hover:text-white`}
                    data-testid={'add-notes-button'}
                >
                    {t('managerAssessmentEvaluation.survey.addNotes')}
                </button>
            )}

            {/* Rendered at all times so the region exists before its text changes: a status node
                inserted together with its content is often missed. */}
            <p
                role={'status'}
                className={NOTE_CAPTION}
                data-testid={`${dataTestId}-status`}
            >
                {statusMessage}
            </p>

            <ConfirmDialog
                isOpen={isConfirmingDelete}
                title={t('managerAssessmentEvaluation.survey.notesDeleteTitle')}
                message={t('managerAssessmentEvaluation.survey.notesDeleteMessage')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                isLoading={deleteNote.isPending}
                isDestructive
                dataTestId={`${dataTestId}-delete-dialog`}
            />
        </div>
    );
};
